import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithToken } from "../api/apiutils";

const C = {
  blue: "#4298D7",
  blueLight: "#ebf4fc",
  red: "#ef4444",
  border: "#e2e8f0",
  label: "#64748b",
  text: "#1e293b",
  muted: "#94a3b8",
  bg: "#f8fafc",
};

/* ── глобальные адаптивные стили (вставляются один раз) ── */
const GLOBAL_STYLE = `
  .ktru-page { padding: 24px 32px; font-family: 'Segoe UI', system-ui, sans-serif; }
  .ktru-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .ktru-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
  @media (max-width: 900px) {
    .ktru-page { padding: 16px 20px; }
    .ktru-grid-2 { grid-template-columns: 1fr; }
    .ktru-grid-3 { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 600px) {
    .ktru-grid-3 { grid-template-columns: 1fr; }
  }
`;

const Field = ({ label, required, hint, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <label
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: C.label,
        letterSpacing: ".4px",
        textTransform: "uppercase",
      }}
    >
      {label}
      {required && <span style={{ color: C.red, marginLeft: 3 }}>*</span>}
    </label>
    {children}
    {hint && <span style={{ fontSize: 11, color: C.muted }}>{hint}</span>}
  </div>
);

const inputBase = {
  width: "100%",
  height: 40,
  padding: "0 12px",
  fontSize: 13,
  color: C.text,
  background: "#fff",
  border: `1.5px solid ${C.border}`,
  borderRadius: 8,
  outline: "none",
  boxSizing: "border-box",
  transition: "border-color .15s, box-shadow .15s",
};

const FInput = ({ style, ...props }) => {
  const [f, setF] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...inputBase,
        ...(f
          ? { borderColor: C.blue, boxShadow: `0 0 0 3px ${C.blueLight}` }
          : {}),
        ...style,
      }}
      onFocus={() => setF(true)}
      onBlur={() => setF(false)}
    />
  );
};

const FSelect = ({ children, style, ...props }) => {
  const [f, setF] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...inputBase,
        cursor: "pointer",
        ...(f
          ? { borderColor: C.blue, boxShadow: `0 0 0 3px ${C.blueLight}` }
          : {}),
        ...style,
      }}
      onFocus={() => setF(true)}
      onBlur={() => setF(false)}
    >
      {children}
    </select>
  );
};

const Section = ({ icon, title, color = C.blue, children }) => (
  <div
    style={{
      background: "#fff",
      border: `1.5px solid ${C.border}`,
      borderRadius: 12,
      overflow: "hidden",
      marginBottom: 16,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "14px 20px",
        background: C.bg,
        borderBottom: `1.5px solid ${C.border}`,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: `${color}1a`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 16,
        }}
      >
        {icon}
      </div>
      <span style={{ fontWeight: 700, fontSize: 14, color: C.text }}>
        {title}
      </span>
    </div>
    <div style={{ padding: "20px", display: "grid", gap: 16 }}>{children}</div>
  </div>
);

/* ════════════════════════════════════════════════════════════════ */
const INITIAL = {
  KtruCode: "",
  KtruName: "",
  SourceName: "",
  SourceUrl: "",
  SourceType: "",
  Priority: 1,
  IsActive: true,
};

const KtruSourceAddPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = useCallback(
    (field) => (e) => {
      const value =
        e.target.type === "checkbox" ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError(null);
      if (
        !form.KtruCode.trim() ||
        !form.SourceName.trim() ||
        !form.SourceUrl.trim()
      ) {
        setError("Заполните все обязательные поля");
        return;
      }
      try {
        setSaving(true);
        const params = new URLSearchParams({
          KtruCode: form.KtruCode,
          KtruName: form.KtruName,
          SourceName: form.SourceName,
          SourceUrl: form.SourceUrl,
          SourceType: form.SourceType,
          Priority: String(form.Priority),
          IsActive: String(form.IsActive),
        });

        const res = await fetchWithToken(
          `/api/ktru-sources?${params.toString()}`,
          {
            method: "POST",
          },
        );
        navigate("/ktru-sources");
      } catch (err) {
        setError(err.message);
      } finally {
        setSaving(false);
      }
    },
    [form, navigate],
  );

  return (
    <>
      <style>{GLOBAL_STYLE}</style>
      <div className="ktru-page">
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <button
            onClick={() => navigate("/ktru-sources")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: C.label,
              fontSize: 13,
              padding: 0,
              marginBottom: 12,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M10 3L5 8l5 5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Назад к списку
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: C.blueLight,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 4v12M4 10h12"
                  stroke={C.blue}
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: 22,
                  fontWeight: 700,
                  color: C.text,
                }}
              >
                Добавить источник
              </h1>
              <p style={{ margin: 0, fontSize: 13, color: C.muted }}>
                Новая запись в справочнике КТРУ
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "12px 16px",
              borderRadius: 10,
              marginBottom: 20,
              background: "#fef2f2",
              border: "1.5px solid #fecaca",
              color: C.red,
              fontSize: 13,
            }}
          >
            <span>⚠️</span> {error}
            <button
              onClick={() => setError(null)}
              style={{
                marginLeft: "auto",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: C.red,
                fontSize: 18,
                lineHeight: 1,
              }}
            >
              ×
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <Section icon="📋" title="Информация о КТРУ">
            <div className="ktru-grid-2">
              <Field label="КТРУ код" required>
                <FInput
                  value={form.KtruCode}
                  onChange={set("KtruCode")}
                  placeholder="329916.100.000001"
                />
              </Field>
              <Field label="Название КТРУ">
                <FInput
                  value={form.KtruName}
                  onChange={set("KtruName")}
                  placeholder="Доска маркерная"
                />
              </Field>
            </div>
          </Section>

          <Section icon="🔗" title="Источник" color="#8b5cf6">
            <div className="ktru-grid-2">
              <Field label="Название источника" required>
                <FInput
                  value={form.SourceName}
                  onChange={set("SourceName")}
                  placeholder="satu.kz"
                />
              </Field>
              <Field label="URL" required>
                <FInput
                  value={form.SourceUrl}
                  onChange={set("SourceUrl")}
                  placeholder="https://satu.kz"
                />
              </Field>
            </div>
            <div className="ktru-grid-3">
              <Field label="Тип источника">
                <FSelect value={form.SourceType} onChange={set("SourceType")}>
                  <option value="">— не указано —</option>
                  <option value="marketplace">Маркетплейс</option>
                  <option value="retailer">Ритейлер</option>
                  <option value="wholesaler">Опт</option>
                </FSelect>
              </Field>
              <Field label="Приоритет" hint="1 = главный, 2 = запасной">
                <FInput
                  type="number"
                  value={form.Priority}
                  onChange={set("Priority")}
                  min={1}
                  max={10}
                />
              </Field>
            </div>
          </Section>

          <Section icon="⚙️" title="Настройки" color="#10b981">
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                background: form.IsActive ? "#f0fdf4" : C.bg,
                border: `1.5px solid ${form.IsActive ? "#bbf7d0" : C.border}`,
                borderRadius: 10,
                cursor: "pointer",
                transition: "all .15s",
                maxWidth: 480,
              }}
            >
              <input
                type="checkbox"
                checked={form.IsActive}
                onChange={set("IsActive")}
                style={{
                  width: 16,
                  height: 16,
                  accentColor: "#10b981",
                  cursor: "pointer",
                }}
              />
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: C.text }}>
                  Источник активен
                </div>
                <div style={{ fontSize: 12, color: C.muted }}>
                  Отключите, если источник временно недоступен
                </div>
              </div>
              <div
                style={{
                  marginLeft: "auto",
                  padding: "2px 10px",
                  borderRadius: 99,
                  fontSize: 11,
                  fontWeight: 700,
                  background: form.IsActive ? "#dcfce7" : "#f1f5f9",
                  color: form.IsActive ? "#15803d" : C.muted,
                }}
              >
                {form.IsActive ? "Вкл" : "Выкл"}
              </div>
            </label>
          </Section>

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "10px 28px",
                borderRadius: 8,
                border: "none",
                background: saving ? C.muted : C.blue,
                color: "#fff",
                fontWeight: 600,
                fontSize: 14,
                cursor: saving ? "not-allowed" : "pointer",
              }}
            >
              {saving ? "Сохранение..." : "Сохранить"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/ktru-sources")}
              disabled={saving}
              style={{
                padding: "10px 22px",
                borderRadius: 8,
                border: `1.5px solid ${C.border}`,
                background: "#fff",
                color: C.label,
                fontWeight: 500,
                fontSize: 14,
                cursor: "pointer",
              }}
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default KtruSourceAddPage;
